import React, { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const Navbar = () => {
  const { language, setLang, t } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
    setIsMobileMenuOpen(false);
  };

  const navItems = [
    { id: 'hero', label: t.nav.home },
    { id: 'course', label: t.nav.course },
    { id: 'favorites', label: t.nav.favorites },
    { id: 'subscribe', label: t.nav.subscribe },
  ];

  return (
    <>
      <motion.nav
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 1, ease: [0.16, 1, 0.3, 1], delay: 0.2 }}
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-700 ${
          isScrolled
            ? 'bg-background/75 backdrop-blur-xl border-b border-border/60'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-[1480px] mx-auto px-6 md:px-12">
          <div className="flex items-center justify-between h-20 md:h-24">
            {/* Wordmark */}
            <button
              onClick={() => scrollToSection('hero')}
              className="font-heading text-xl md:text-[1.4rem] tracking-wide text-text-primary hover:text-accent transition-colors duration-500"
              data-testid="navbar-logo"
            >
              Renata <span className="italic font-light">Hamikus</span>
            </button>

            {/* Desktop Nav */}
            <div className="hidden md:flex items-center gap-10">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className="link-underline font-body text-[0.78rem] uppercase tracking-[0.22em] text-text-primary/80 hover:text-text-primary transition-colors duration-500"
                  data-testid={`nav-${item.id}`}
                >
                  {item.label}
                </button>
              ))}
            </div>

            {/* Right cluster */}
            <div className="flex items-center gap-5 md:gap-7">
              {/* Language */}
              <div className="hidden sm:flex items-center gap-2 font-body text-[0.72rem] tracking-[0.2em]" data-testid="language-switcher">
                <button
                  onClick={() => setLang('hu')}
                  className={`transition-colors duration-500 ${
                    language === 'hu' ? 'text-accent' : 'text-text-secondary hover:text-text-primary'
                  }`}
                  data-testid="lang-hu"
                >
                  HU
                </button>
                <span className="text-border">/</span>
                <button
                  onClick={() => setLang('en')}
                  className={`transition-colors duration-500 ${
                    language === 'en' ? 'text-accent' : 'text-text-secondary hover:text-text-primary'
                  }`}
                  data-testid="lang-en"
                >
                  EN
                </button>
              </div>

              {/* Work With Me CTA - desktop */}
              <button
                onClick={() => scrollToSection('work-with-me')}
                className="hidden md:inline-flex items-center gap-3 group"
                data-testid="navbar-work-cta"
              >
                <span className="font-body text-[0.72rem] uppercase tracking-[0.22em] text-text-primary">
                  {t.nav.workWithMe}
                </span>
                <span className="relative inline-block w-2 h-2 rounded-full bg-accent shadow-[0_0_0_4px_rgba(197,160,89,0.15)] group-hover:shadow-[0_0_0_8px_rgba(197,160,89,0.2)] transition-all duration-700" />
              </button>

              {/* Mobile menu */}
              <button
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="md:hidden p-2 text-text-primary hover:text-accent transition-colors"
                data-testid="mobile-menu-toggle"
                aria-label="Toggle menu"
              >
                {isMobileMenuOpen ? <X size={22} strokeWidth={1.25} /> : <Menu size={22} strokeWidth={1.25} />}
              </button>
            </div>
          </div>
        </div>
      </motion.nav>

      {/* Mobile menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            className="fixed inset-0 z-40 bg-background md:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col items-center justify-center h-full gap-8 px-6">
              {[...navItems, { id: 'work-with-me', label: t.nav.workWithMe }].map((item, index) => (
                <motion.button
                  key={item.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + index * 0.08, duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                  onClick={() => scrollToSection(item.id)}
                  className="font-heading text-4xl text-text-primary hover:text-accent transition-colors"
                  data-testid={`mobile-nav-${item.id}`}
                >
                  {item.label}
                </motion.button>
              ))}

              {/* Language switch mobile */}
              <div className="mt-8 flex items-center gap-3 font-body text-xs tracking-[0.25em]">
                <button onClick={() => setLang('hu')} className={language === 'hu' ? 'text-accent' : 'text-text-secondary'} data-testid="mobile-lang-hu">HU</button>
                <span className="text-border">/</span>
                <button onClick={() => setLang('en')} className={language === 'en' ? 'text-accent' : 'text-text-secondary'} data-testid="mobile-lang-en">EN</button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
